import { useState, useEffect, useCallback } from 'react';
import { MapPin, ChevronDown, Loader2, AlertCircle } from 'lucide-react';
import { locationService, District, Subdistrict, Village } from '@/services/locationService';

export interface LocationFilterValue {
  district?: string;
  subdistrict?: string;
  village_id?: string;
  village_name?: string;
}

export interface LocationFilterProps {
  value: LocationFilterValue;
  onChange: (value: LocationFilterValue) => void;
  showVillages?: boolean;
  disabled?: boolean;
  className?: string;
}

export const LocationFilter: React.FC<LocationFilterProps> = ({
  value,
  onChange,
  showVillages = true,
  disabled = false,
  className = ""
}) => {
  const [districts, setDistricts] = useState<District[]>([]);
  const [subdistricts, setSubdistricts] = useState<Subdistrict[]>([]);
  const [villages, setVillages] = useState<Village[]>([]);
  
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingSubdistricts, setLoadingSubdistricts] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);
  
  const [error, setError] = useState<string | null>(null);

  // Load districts on component mount
  useEffect(() => {
    loadDistricts();
  }, []);

  // Load subdistricts when district changes
  useEffect(() => {
    if (value.district) {
      loadSubdistricts(value.district);
    } else {
      setSubdistricts([]);
    }
  }, [value.district]);

  // Load villages when subdistrict changes
  useEffect(() => {
    if (value.district && value.subdistrict && showVillages) {
      loadVillages(value.district, value.subdistrict);
    } else {
      setVillages([]);
    }
  }, [value.district, value.subdistrict, showVillages]);

  const loadDistricts = useCallback(async () => {
    try {
      setLoadingDistricts(true);
      setError(null);
      
      const districtData = await locationService.getDistricts();
      setDistricts(districtData || []);
    } catch (err) {
      console.error('Error loading districts:', err);
      setError('Failed to load districts');
    } finally {
      setLoadingDistricts(false);
    }
  }, []);

  const loadSubdistricts = useCallback(async (districtName: string) => {
    try {
      setLoadingSubdistricts(true);
      setError(null);
      
      const response = await locationService.getSubdistricts(districtName);
      setSubdistricts(response?.data?.list || []);
    } catch (err) {
      console.error('Error loading subdistricts:', err);
      setError('Failed to load subdistricts');
    } finally {
      setLoadingSubdistricts(false);
    }
  }, []);

  const loadVillages = useCallback(async (districtName: string, subdistrictName: string) => {
    try {
      setLoadingVillages(true);
      setError(null);
      
      const response = await locationService.getVillages(districtName, subdistrictName);
      setVillages(response?.data?.list || []);
    } catch (err) {
      console.error('Error loading villages:', err);
      setError('Failed to load villages');
    } finally {
      setLoadingVillages(false);
    }
  }, []);

  const handleDistrictChange = useCallback((district: string) => {
    onChange({
      district: district || undefined,
      subdistrict: undefined,
      village_id: undefined,
      village_name: undefined
    });
  }, [onChange]);

  const handleSubdistrictChange = useCallback((subdistrict: string) => {
    onChange({
      ...value,
      subdistrict: subdistrict || undefined,
      village_id: undefined,
      village_name: undefined
    });
  }, [value, onChange]);

  const handleVillageChange = useCallback((village_id: string) => {
    const selectedVillage = villages.find(v => v.village_id === village_id);
    onChange({
      ...value,
      village_id: village_id || undefined,
      village_name: selectedVillage?.village_name || undefined
    });
  }, [value, villages, onChange]);

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center mb-4">
        <MapPin className="h-5 w-5 text-gray-400 mr-2" />
        <h4 className="font-medium text-gray-900">Location</h4>
      </div>

      {error && (
        <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-md">
          <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      <div className="space-y-4">
        {/* District */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            District
          </label>
          <div className="relative">
            <select
              disabled={disabled || loadingDistricts}
              value={value.district || ''}
              onChange={(e) => handleDistrictChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 appearance-none pr-8"
            >
              <option value="">Select District</option>
              {districts.map((district, index) => {
                const districtName = typeof district === 'string' ? district : district.name || district.id;
                return (
                  <option key={index} value={districtName}>
                    {districtName}
                  </option>
                );
              })}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              {loadingDistricts ? (
                <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </div>
          </div>
        </div>

        {/* Subdistrict */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sub-district
          </label>
          <div className="relative">
            <select
              disabled={disabled || !value.district || loadingSubdistricts}
              value={value.subdistrict || ''}
              onChange={(e) => handleSubdistrictChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 appearance-none pr-8"
            >
              <option value="">Select Sub-district</option>
              {subdistricts.map((subdistrict, index) => (
                <option key={index} value={subdistrict.subdistrict_name}>
                  {subdistrict.subdistrict_name}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              {loadingSubdistricts ? (
                <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </div>
          </div>
        </div>

        {/* Village */}
        {showVillages && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Village
            </label>
            <div className="relative">
              <select
                disabled={disabled || !value.subdistrict || loadingVillages}
                value={value.village_id || ''}
                onChange={(e) => handleVillageChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 appearance-none pr-8"
              >
                <option value="">Select Village</option>
                {villages.map((village, index) => (
                  <option key={index} value={village.village_id}>
                    {village.village_name} ({village.village_id})
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                {loadingVillages ? (
                  <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};